import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  LinkSimple, CircleNotch, Sparkle, PaperPlaneTilt, PencilSimple, Trash, WarningCircle,
} from "@phosphor-icons/react";

const STATUS = ["prospect", "drafted", "sent", "replied", "won", "lost"];
const statusColor = {
  prospect: "var(--text-2)", drafted: "var(--warning)", sent: "var(--primary)",
  replied: "var(--accent)", won: "var(--success)", lost: "var(--danger)",
};
const typeLabel = { guest_post: "Guest Post", directory: "Directory", partner: "Partner", resource_page: "Resource", pr: "PR", sponsorship: "Sponsor" };

export default function LinkBuilding() {
  const { currentWorkspace } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState("");
  const [count, setCount] = useState(8);
  const [emailCfg, setEmailCfg] = useState(true);
  const [editing, setEditing] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => api.listOutreach().then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.outreachConfig().then((r) => setEmailCfg(r.data.email_configured)).catch(() => {});
  }, [currentWorkspace?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.generateProspects(Number(count), focus);
      const { created, unreachable_dropped } = r.data;
      if (unreachable_dropped > 0) {
        toast.success(`Added ${created} prospects (${unreachable_dropped} unreachable site${unreachable_dropped === 1 ? "" : "s"} skipped)`);
      } else {
        toast.success(`Added ${created} prospects`);
      }
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const openEdit = (it) => setEditing({ ...it });

  const draft = async () => {
    setDrafting(true);
    try {
      const r = await api.draftOutreach(editing.id);
      setEditing({ ...editing, subject: r.data.subject, body: r.data.body, status: "drafted" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setDrafting(false); }
  };

  const saveEdit = async () => {
    try {
      await api.updateOutreach(editing.id, { contact_email: editing.contact_email, subject: editing.subject, body: editing.body });
      toast.success("Saved"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const send = async () => {
    setSending(true);
    try {
      await api.updateOutreach(editing.id, { contact_email: editing.contact_email, subject: editing.subject, body: editing.body });
      await api.sendOutreach(editing.id);
      toast.success("Email sent"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const setStatus = async (it, status) => {
    setItems((p) => p.map((x) => x.id === it.id ? { ...x, status } : x));
    try { await api.updateOutreach(it.id, { status }); } catch { load(); }
  };
  const del = async (id) => { await api.deleteOutreach(id); load(); };

  return (
    <div className="fade-up space-y-6">
      <div className="border p-6 bg-[var(--surface)] flex flex-col lg:flex-row lg:items-end gap-4" style={{ borderColor: "var(--line)" }}>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-11 h-11 flex items-center justify-center shrink-0" style={{ background: "#0b1220", borderRadius: 3 }}><LinkSimple size={24} weight="duotone" color="var(--accent)" /></div>
          <div>
            <h2 className="text-base font-bold">Link Building Outreach</h2>
            <p className="text-sm" style={{ color: "var(--text-2)" }}>AI finds backlink prospects, drafts personalized emails, and enrols them in your Instantly outreach campaign.</p>
          </div>
        </div>
        <input data-testid="outreach-focus" value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Optional focus e.g. MSP directories" className="text-sm px-3 py-2.5 border bg-[#0b1220] outline-none w-full lg:w-64" style={{ borderColor: "var(--line)" }} />
        <select value={count} onChange={(e) => setCount(e.target.value)} className="text-sm px-3 py-2.5 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }}>
          {[5, 8, 10, 12].map((n) => <option key={n} value={n}>{n} prospects</option>)}
        </select>
        <button data-testid="generate-prospects" onClick={generate} disabled={loading} className="px-6 py-2.5 text-sm font-bold flex items-center gap-2 justify-center text-white disabled:opacity-60 hover:opacity-90 transition-opacity shrink-0" style={{ background: "var(--primary)" }}>
          {loading ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="bold" />} Find Prospects
        </button>
      </div>

      {!emailCfg && (
        <div className="border px-4 py-3 flex items-center gap-2 text-sm" style={{ borderColor: "var(--warning)", background: "rgba(250,204,21,0.06)", color: "var(--warning)" }} data-testid="email-not-configured">
          <WarningCircle size={18} weight="duotone" /> Direct send is off — drafts are saved. Connect an Instantly campaign in Settings to send.
        </div>
      )}

      {items.length === 0 ? (
        <div className="border p-12 text-center bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
          <LinkSimple size={34} weight="duotone" color="var(--text-2)" className="mx-auto mb-3" />
          <p className="text-sm" style={{ color: "var(--text-2)" }}>No prospects yet — click Find Prospects to build your backlink pipeline.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} data-testid="outreach-card" className="border p-4 bg-[var(--surface)] flex flex-col" style={{ borderColor: "var(--line)" }}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{it.name}</div>
                  <a href={it.url?.startsWith("http") ? it.url : `https://${it.url}`} target="_blank" rel="noreferrer" className="mono text-[11px] truncate block hover:underline" style={{ color: "var(--accent)" }}>{it.url}</a>
                </div>
                <span className="mono text-[10px] px-1.5 py-0.5 border shrink-0" style={{ borderColor: "var(--line)", color: "var(--text-2)" }}>{typeLabel[it.type] || it.type}</span>
              </div>
              <p className="text-xs mt-2 flex-1" style={{ color: "var(--text-2)" }}>{it.reason}</p>
              <div className="flex items-center gap-2 mt-3">
                <span className="w-2 h-2 rounded-full" style={{ background: statusColor[it.status] }} />
                <select value={it.status} onChange={(e) => setStatus(it, e.target.value)} data-testid="outreach-status" className="flex-1 text-[11px] mono px-2 py-1 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }}>
                  {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button data-testid="edit-outreach" onClick={() => openEdit(it)} title="Draft / edit email" className="p-1.5 border hover:bg-[#0b1220] transition-colors" style={{ borderColor: "var(--line)" }}><PencilSimple size={14} /></button>
                <button data-testid="delete-outreach" onClick={() => del(it.id)} className="p-1.5 border hover:bg-[#0b1220] transition-colors" style={{ borderColor: "var(--line)", color: "var(--danger)" }}><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[var(--surface)] border-[var(--line)] text-[var(--text)] max-w-2xl">
          <DialogHeader><DialogTitle>Outreach — {editing?.name}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input data-testid="outreach-email" value={editing.contact_email} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} placeholder="contact@site.com" className="flex-1 text-sm px-3 py-2 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }} />
                <button data-testid="draft-outreach" onClick={draft} disabled={drafting} className="text-xs flex items-center gap-1 px-3 py-2 border hover:bg-[#0b1220] transition-colors" style={{ borderColor: "var(--line)" }}>{drafting ? <CircleNotch size={13} className="animate-spin" /> : <Sparkle size={13} />} AI draft</button>
              </div>
              <input data-testid="outreach-subject" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} placeholder="Subject" className="w-full text-sm px-3 py-2 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }} />
              <textarea data-testid="outreach-body" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={10} placeholder="Email body — use AI draft or write your own" className="w-full text-sm px-3 py-2 border bg-[#0b1220] outline-none resize-none" style={{ borderColor: "var(--line)" }} />
              <div className="flex items-center justify-between">
                <button onClick={saveEdit} className="text-sm px-4 py-2 border hover:bg-[#0b1220] transition-colors" style={{ borderColor: "var(--line)" }}>Save draft</button>
                <button data-testid="send-outreach" onClick={send} disabled={sending} className="text-sm px-5 py-2 font-bold flex items-center gap-2 text-white disabled:opacity-60" style={{ background: "var(--primary)" }}>{sending ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />} Send</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
